import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { dateQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { DEFAULT_WINDOW_MINUTES } from "components/manualReview/hooks/useManualReviewNavigation";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useMemo } from "react";

dayjs.extend(utc);

const HALF_WINDOW_MINUTES = DEFAULT_WINDOW_MINUTES / 2;

export type DayWindowNavigation = Readonly<{
  anchorTime: Dayjs;
  setAnchorTime: (value: Dayjs) => void;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  dayStart: Dayjs;
  dayEnd: Dayjs;
  dayStartIso: string;
  onWindowChange: (newStart: Dayjs) => void;
}>;

export const useDayWindowNavigation = (
  key: string,
  makeInitialAnchor: () => Dayjs
): DayWindowNavigation => {
  const [anchorTime, setAnchorTime] = useNavigationQuery(
    key,
    makeInitialAnchor,
    dateQuery,
    "replace"
  );

  const windowStart = useMemo(
    () => anchorTime.subtract(HALF_WINDOW_MINUTES, "minute"),
    [anchorTime]
  );
  const windowEnd = useMemo(
    () => anchorTime.add(HALF_WINDOW_MINUTES, "minute"),
    [anchorTime]
  );
  const dayStart = useMemo(() => anchorTime.startOf("day"), [anchorTime]);
  const dayEnd = useMemo(() => dayStart.add(1, "day"), [dayStart]);
  const dayStartIso = useMemo(() => dayStart.toISOString(), [dayStart]);

  const onWindowChange = useCallback(
    (newStart: Dayjs) => {
      setAnchorTime(newStart.add(HALF_WINDOW_MINUTES, "minute"));
    },
    [setAnchorTime]
  );

  return {
    anchorTime,
    setAnchorTime,
    windowStart,
    windowEnd,
    dayStart,
    dayEnd,
    dayStartIso,
    onWindowChange,
  };
};
